import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import axios from "../axiosConfig";
import { BASE_SERVER_URL, weekDays } from "../sampleData";
import { PageLoading, orderObject } from "../components";
import { PopUpAlert } from "../components/movableDragDropArea";
import ProductPickerModal from "../components/ProductPickerModal";
import ReviewOrder from "./reviewOrderPage";
import SearchSelect from "../components/SearchSelect";
import AddressAutocomplete from "../components/AddressAutocomplete";
import DateField from "../components/DateField";
import SelectMenu from "../components/SelectMenu";
import { useAuth } from "../auth/AuthContext";
import { roleOf } from "../auth/permissions";
import { useActiveBusiness } from "../auth/useActiveBusiness";
import { defaultOrderTypeFor } from "../businessBranding";
import { PAYMENT_OPTIONS, RETAIL_PAYMENT_OPTIONS } from "../paymentOptions";
import { casketTypesOf, isCasketCover, lineGoesWithCasket } from "../productCategories";
import {
  commissionOf,
  estimateFdCommission,
  pctLabel,
} from "../commission";
import "../new-order.css";

/* ------------------------------------------------------------------ *
 * New order (redesign) — /new-order
 *
 * A second entry screen alongside pages.js FuneralInputPage (still live at
 * /FuneralOrderInput) and RecurringInputPage (/RetailOrderInput). The
 * Funeral / Retail toggle switches the form in place — retail swaps the
 * funeral cards for company + recipient + schedule fields and submits the
 * same payload RecurringInputPage does. Same endpoints either way:
 *   GET  /settings/getAll          gst, delivery_fee, sash_prices
 *   GET  /fd/getAll                funeral directors
 *   POST /fd/addNew                inline "add a director"
 *   GET  /wo/searchFunerals/:q     existing funerals, for the name field:
 *                                  name matches only, at most 50, newest
 *                                  first; with no :q, the upcoming ones
 *   POST /products/getAllBy        catalogue (inside ProductPickerModal)
 *   POST /wo/addNew                submit
 *
 * With `editWo` (routes /editworkorder/:type/:id) the same screen edits an
 * existing order, keeping the legacy edit contract:
 *   POST /wo/getBy                 the order
 *   POST /wo/getProductsBy         its lines, flagged `fromDB`
 *   POST /wo/updateWO              submit — added lines carry `newPrdt`,
 *                                  removed saved lines stay in the array
 *                                  flagged `deleted`
 *
 * What the redesign changes is the shape of the screen, not the data:
 *   - one flat column of ~25 inputs -> a step bar and two grouped cards
 *   - paired delivery checkboxes    -> segmented destination pickers
 *   - product overlay + tables      -> ProductPickerModal + a live rail
 *   - free-text FD and location     -> SearchSelect with inline create
 *   - plain late-person input       -> search + a pinned record card, so a
 *                                      second order for the same funeral
 *                                      links to the parent instead of
 *                                      silently duplicating it
 * ------------------------------------------------------------------ */

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/* Payment methods that bill someone later, so they need an invoice email —
   the same pairing DropDown enforced on the old page. */
const EMAIL_PAYMENTS = ["Account", "Invoice Family Direct"];

/* Retail (RecurringInputPage) only needs an email for Account. */
const RETAIL_EMAIL_PAYMENTS = ["Account"];
const DELIVERY_AREAS = ["North", "City", "South", "West"];
const FREQUENCIES = ["Weekly", "Fortnightly"];

/* The order-group fields each type submits — orderObject's canonical lists.
   Both types share one form state, so the payload is filtered per type and
   toggling Funeral <-> Retail never leaks a field across. */
const FUNERAL_ORDER_FIELDS = [
  "late_person_name",
  "ordered_by",
  "orderer_phone",
  "payment_option",
  "payment_email",
  "payment_date",
  "invoice_number",
];
const RETAIL_ORDER_FIELDS = [
  "company_name",
  "ordered_by",
  "orderer_phone",
  "payment_option",
  "payment_email",
  "payment_date",
  "invoice_number",
  "card_message",
  "order_comment",
];

function pickFields(obj, fields) {
  const out = {};
  fields.forEach((k) => {
    if ((obj || {})[k] !== undefined) out[k] = obj[k];
  });
  return out;
}


function initials(name) {
  return (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

/* Locations are stored as JSON — { name, coordinates } — but older rows and
   hand-typed values are bare strings. */
function locationName(value) {
  if (!value) return "";
  try {
    const parsed = JSON.parse(value);
    return parsed.name || "";
  } catch (err) {
    return value;
  }
}

function parseJSON(value, fallback) {
  try {
    return JSON.parse(value || "");
  } catch (err) {
    return fallback;
  }
}

const DEST_CHOICES = ["Funeral director", "Service venue", "Other address"];

/* The instructions saved on a director's profile. The inline "add a
   director" form stores its billing kind in the same field — that isn't an
   instruction, so it never pops up. */
const DIRECTOR_KIND_TAGS = ["Account customer", "Pay per order"];
function directorNote(director) {
  const note = String((director || {}).unique_info || "").trim();
  return note && !DIRECTOR_KIND_TAGS.includes(note) ? note : "";
}

/* Which destinations a funeral order's lines need: anything that goes with
   the casket cover (Settings → Product types, src/productCategories.js —
   unless the line was added as a tribute) goes where the casket cover goes;
   everything else where the tributes go — the same split the work order
   list and run sheets make. */
function destinationsNeeded(lines, casketTypes) {
  return {
    cc: lines.some((l) => lineGoesWithCasket(l, casketTypes)),
    tributes: lines.some((l) => !lineGoesWithCasket(l, casketTypes)),
  };
}

/* Which destination segment a saved delivery location corresponds to. */
function destChoice(location, venue, fdLocation) {
  if (!location) return "";
  if (location === venue) return "Service venue";
  if (location === fdLocation) return "Funeral director";
  return "Other address";
}

/* The app stores dates as DD/MM/YYYY; ISO is easier to hand to Date. */
function toISODate(value) {
  const parts = String(value || "").split("/");
  if (parts.length !== 3) return "";
  const [d, m, y] = parts;
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
}

function daysFromToday(ddmmyyyy) {
  const iso = toISODate(ddmmyyyy);
  if (!iso) return null;
  const then = new Date(`${iso}T00:00:00`);
  if (isNaN(then.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((then - today) / 86400000);
}

function Step({ n, label, state, onClick }) {
  return (
    <button type="button" className={`rf-step is-${state}`} onClick={onClick}>
      <span className="rf-step-badge">{state === "done" ? "✓" : n}</span>
      <span className="rf-step-label">{label}</span>
      <span className="rf-step-line" />
    </button>
  );
}

function Field({
  label,
  required,
  value,
  placeholder,
  affordance,
  hint,
  hintTone,
  error,
  type,
  span,
  area,
  children,
  onChange,
}) {
  return (
    <div className="rf-field" style={span ? { gridColumn: span } : {}}>
      <label className="rf-label">
        {label}
        {required && <span className="rf-req">•</span>}
      </label>
      <div
        className={`rf-input-shell${error ? " is-error" : ""}${
          area ? " is-area" : ""
        }`}
      >
        {children || (
          <input
            type={type || "text"}
            value={value || ""}
            placeholder={placeholder}
            onChange={(e) => onChange && onChange(e.target.value)}
          />
        )}
        {affordance && <span className="rf-affordance">{affordance}</span>}
      </div>
      {hint && (
        <span className={`rf-hint${hintTone ? ` is-${hintTone}` : ""}`}>
          {hint}
        </span>
      )}
    </div>
  );
}

/* A field whose value is chosen from a SearchSelect rather than typed. */
function PickerField({
  label,
  required,
  value,
  placeholder,
  hint,
  hintTone,
  error,
  open,
  onOpen,
  children,
  span,
  direction,
}) {
  return (
    <div
      className="rf-field rf-field-picker"
      style={span ? { gridColumn: span } : {}}
    >
      <label className="rf-label">
        {label}
        {required && <span className="rf-req">•</span>}
      </label>
      <div
        className={`rf-input-shell is-picker${open ? " is-open" : ""}${
          error ? " is-error" : ""
        }`}
        onClick={onOpen}
      >
        {value ? (
          <span className="rf-picker-value">{value}</span>
        ) : (
          <span className="rf-picker-placeholder">{placeholder}</span>
        )}
        <span className="rf-affordance">▾</span>
      </div>
      {hint && (
        <span className={`rf-hint${hintTone ? ` is-${hintTone}` : ""}`}>
          {hint}
        </span>
      )}
      {open && (
        <div className={`rf-picker-panel is-${direction || "down"}`}>
          {children}
        </div>
      )}
    </div>
  );
}

/* Replaces the name search field once an existing funeral is linked. */
function PersonCard({ record, onClear, onChange }) {
  const facts = [
    { k: "Funeral director", v: record.fd || "—" },
    { k: "Service", v: record.service || "—" },
    { k: "Venue", v: record.venue || "—" },
  ];
  return (
    <div className="rf-person">
      <span className="rf-person-chip">{initials(record.name)}</span>

      <div className="rf-person-main">
        <div className="rf-person-head">
          <span className="rf-serif rf-person-name">{record.name}</span>
          <span className="rf-person-badge">{record.status}</span>
          {record.dates && (
            <span className="rf-person-dates">{record.dates}</span>
          )}
        </div>

        <div className="rf-person-facts">
          {facts.map((f) => (
            <div key={f.k} className="rf-person-fact">
              <div className="rf-person-fact-k">{f.k}</div>
              <div className="rf-person-fact-v">{f.v}</div>
            </div>
          ))}
        </div>

        {record.linked > 0 && (
          <div className="rf-person-warn">
            <span className="rf-person-warn-bar" />
            <span>
              {record.linked} order{record.linked > 1 ? "s" : ""} already taken
              for this funeral — check you&rsquo;re not duplicating a tribute.
            </span>
            <button
              type="button"
              className="rf-link"
              onClick={() =>
                window.open(`/work-order/funeral/${record.serial}`, "_blank")
              }
            >
              View
            </button>
          </div>
        )}
      </div>

      <div className="rf-person-actions">
        <button
          type="button"
          className="rf-icon-btn is-small"
          onClick={onClear}
          aria-label="Clear"
        >
          ×
        </button>
        <button type="button" className="rf-link" onClick={onChange}>
          Change
        </button>
      </div>
    </div>
  );
}

/* One of the two delivery destination cards. `choice` drives which address
   fills it; "Other address" is a Google address search, like the funeral
   location. */
function DestinationCard({
  title,
  required,
  choice,
  onChoice,
  address,
  onAddress,
  time,
  onTime,
}) {
  return (
    <div className="rf-dest">
      <div className="rf-dest-head">
        <span className="rf-dest-title">
          {title}
          {required && <span className="rf-req">•</span>}
        </span>
        <div className="rf-input-shell rf-dest-time-input">
          <input
            value={time || ""}
            placeholder="Time"
            onChange={(e) => onTime(e.target.value)}
          />
        </div>
      </div>
      <div className="rf-segmented is-full">
        {DEST_CHOICES.map((o) => (
          <button
            key={o}
            type="button"
            className={choice === o ? "is-active" : ""}
            onClick={() => onChoice(o)}
          >
            {o}
          </button>
        ))}
      </div>
      {choice === "Other address" ? (
        <div className="rf-input-shell rf-dest-input">
          <AddressAutocomplete
            value={address}
            placeholder="Search a venue or street address"
            onChange={onAddress}
          />
        </div>
      ) : (
        <div className="rf-dest-address">
          {address || "Pick a funeral director and venue above"}
        </div>
      )}
    </div>
  );
}

export function NewOrderPage(props) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(props.editWo);
  const { user, memberships } = useAuth();
  const currentUser = props.current_user || user;
  document.title = props.title || (isEdit ? "Edit work order" : "New work order");

  /* ---------- the payload, in the exact shape /wo/addNew expects ---------- */
  const [orderDetail, setOrderDetail] = useState({
    order: { late_person_name: "" },
    price_detail: {},
    datetime: {
      funeral_date: "",
      funeral_time: "",
      funeral_location: "",
    },
    fd_code: { "FD Code": { code: "" } },
    /* one-time delivery is the default; repeating is the opt-in. The day
       starts as "" (not absent): /wo/addNew turns any value other than ""
       into Number(value), and Number(undefined) is NaN, which the schema
       rejects — the retail form could not save at all without it. */
    recipient: { frequency: "Once", delivery_day: "" },
  });
  /* Funeral | Retail — the toggle swaps the form in place. A route that
     names a type wins; otherwise the open business picks (Retail for The
     Floral Decorator). */
  const activeBusiness = useActiveBusiness();
  const [orderType, setOrderType] = useState(
    props.orderType || defaultOrderTypeFor(activeBusiness)
  );
  /* The business can resolve after the first render (memberships load with
     the session) — follow it until someone picks a type themselves. */
  const [typeTouched, setTypeTouched] = useState(false);
  useEffect(() => {
    if (props.orderType || isEdit || typeTouched) return;
    setOrderType(defaultOrderTypeFor(activeBusiness));
  }, [activeBusiness, props.orderType, isEdit, typeTouched]);
  /* The business a NEW order is created in: the open one by default, or
     any other business this person is an owner or admin of. Editing keeps
     the order where it is — moving one is a transfer. */
  const [targetBusinessId, setTargetBusinessId] = useState(
    activeBusiness ? String(activeBusiness.id) : ""
  );
  useEffect(() => {
    if (!targetBusinessId && activeBusiness) setTargetBusinessId(String(activeBusiness.id));
  }, [activeBusiness, targetBusinessId]);
  const createInOptions = useMemo(() => {
    if (isEdit) return [];
    const activeId = activeBusiness ? String(activeBusiness.id) : "";
    return (memberships || [])
      .filter(
        (m) =>
          String(m.business_id) === activeId || ["owner", "admin"].includes(roleOf(m))
      )
      .map((m) => ({
        value: String(m.business_id),
        label: m.business_name || m.business_code || "Business",
      }));
  }, [memberships, activeBusiness, isEdit]);
  const targetLabel =
    (createInOptions.find((o) => o.value === targetBusinessId) || {}).label || "";
  const inOtherBusiness =
    !isEdit &&
    Boolean(activeBusiness) &&
    Boolean(targetBusinessId) &&
    targetBusinessId !== String(activeBusiness.id);
  /* Every request this form makes goes to that business — the server checks
     the person's role there, reads its directors, products and settings,
     and files the order under it. */
  function bizConfig() {
    return !isEdit && targetBusinessId
      ? { headers: { "business-id": targetBusinessId } }
      : {};
  }
  const loadedOnce = useRef(false);
  const [oneTimeDelivery, setOneTimeDelivery] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]); // casket covers / retail arrangements
  const [selectedTribuites, setSelectedTribuites] = useState([]);
  const [selectedParentOrder, setSelectedParentOrder] = useState(null);
  const [selectedParentOrderID, setSelectedParentOrderID] = useState("");
  const [deliveryFee, setDeliveryFee] = useState({});
  /* Funeral director commission. The client only ever sends the boolean —
     the server reads the rate off the director and snapshots it onto the
     order. `loadedCommission` is what the saved order carries, so an edit
     can tell whether the box (or the director under it) actually changed. */
  const [applyFdCommission, setApplyFdCommission] = useState(false);
  const [loadedCommission, setLoadedCommission] = useState(null);

  /* ---------- reference data ---------- */
  const [settings, setSettings] = useState({});
  const [directors, setDirectors] = useState([]);
  const [funerals, setFunerals] = useState([]);
  const [funeralsLoading, setFuneralsLoading] = useState(false);
  /* the funeral search in flight: only the latest answer may land */
  const funeralSearch = useRef({ seq: 0, controller: null });
  const [pageLoading, setPageLoading] = useState(true);

  /* ---------- screen state ---------- */
  const [picker, setPicker] = useState(null); // 'person' | 'fd'
  const [modalGroup, setModalGroup] = useState(null);
  /* { group, index, line } while a rail line is open in the picker */
  const [editingLine, setEditingLine] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({});
  /* { name, note } — the picked director's special instructions, shown
     once as a dialog so they can't be missed */
  const [fdNote, setFdNote] = useState(null);
  const [ccChoice, setCcChoice] = useState("");
  const [triChoice, setTriChoice] = useState("");

  const order = orderDetail.order;
  const datetime = orderDetail.datetime;
  const recipient = orderDetail.recipient || {};
  const fd = orderDetail.fd_code["FD Code"] || {};

  function setProperty(a, b) {
    setOrderDetail((prev) => {
      const newObj = { ...prev };
      newObj[b] = { ...(prev[b] || {}) };
      newObj[b][a.name] = a.value;
      return newObj;
    });
  }

  /* ---------- load: settings, directors and funerals belong to the
     business the order goes into, so they reload when it changes ---------- */
  useEffect(() => {
    const settingsLoad = axios
      .get(BASE_SERVER_URL + "/settings/getAll", bizConfig())
      .then((doc) => {
        const doc0 = doc.data.status === "success" ? doc.data.response[0] || {} : {};
        setSettings(doc0);
        return doc0;
      })
      .catch(() => ({}));

    const directorsLoad = axios
      .get(BASE_SERVER_URL + "/fd/getAll", bizConfig())
      .then((doc) => setDirectors(doc.data.response || []))
      .catch(() => {});

    if (!loadedOnce.current) {
      loadedOnce.current = true;
      /* an edit splits its saved lines by the settings' casket types, so
         the settings come first */
      Promise.all([
        directorsLoad,
        isEdit ? settingsLoad.then((doc0) => populate(id, doc0)) : null,
      ]).finally(() => setPageLoading(false));
    }

    searchFunerals("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetBusinessId]);

  /* Switching business: the director, the linked funeral and the products
     are records of the old business, so they go. Everything typed by hand
     (names, dates, times, addresses) stays. */
  function chooseBusiness(next) {
    if (!next || next === targetBusinessId) return;
    const nextLabel =
      (createInOptions.find((o) => o.value === next) || {}).label || "that business";
    const hasRecords =
      selectedProducts.some((l) => !l.deleted) ||
      selectedTribuites.some((l) => !l.deleted) ||
      Boolean(fd._id || fd.code) ||
      Boolean(selectedParentOrderID);
    if (
      hasRecords &&
      !window.confirm(
        `Create this order in ${nextLabel}? The funeral director, linked funeral and products you picked belong to ${targetLabel} and will be cleared.`
      )
    ) {
      return;
    }
    setTargetBusinessId(next);
    setSelectedProducts([]);
    setSelectedTribuites([]);
    setSelectedParentOrder(null);
    setSelectedParentOrderID("");
    setProperty({ name: "FD Code", value: { code: "" } }, "fd_code");
    setApplyFdCommission(false);
    setDeliveryFee({});
    setSettings({});
    setDirectors([]);
    setFunerals([]);
    setPicker(null);
  }

  useEffect(() => {
    setProperty(
      { name: "gst", value: (Number(settings.gst) || 0) / 100 },
      "price_detail"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  useEffect(() => {
    setProperty(
      { name: "delivery_fee", value: deliveryFee.delivery_fee || "" },
      "price_detail"
    );
    setProperty(
      {
        name: "custom_delivery_fee",
        value: deliveryFee.custom_delivery_fee || "",
      },
      "price_detail"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryFee]);

  /* ---------- edit mode: load the saved order and its lines ---------- */
  /* The server splits an order's lines into the two halves by type: the
     casket cover's half is the Casket cover type plus whatever the business
     files under "Casket cover" in Settings → Product types — minus lines
     stamped `as_tribute`, which come back with the tributes. */
  function fetchLines(selectType, category, casketList) {
    return axios
      .post(BASE_SERVER_URL + "/wo/getProductsBy", {
        filter: { work_order_id: id },
        fetchType: selectType,
        ...(selectType === "products"
          ? {}
          : { type: category, cc_types: casketList }),
      })
      .then((res) =>
        (res.data.response || []).map((each) => ({ ...each, fromDB: true }))
      );
  }

  function populate(orderId, settingsDoc) {
    const casketList = ["Casket cover", ...casketTypesOf(settingsDoc)];
    const loadOrder = axios
      .post(BASE_SERVER_URL + "/wo/getBy", { filter: { _id: orderId } })
      .then((doc) => {
        const raw = (doc.data.response || [])[0];
        if (!raw) throw new Error("Work order not found");
        const data = orderObject(raw, orderType);
        setDeliveryFee({
          delivery_fee: data.price_detail.delivery_fee,
          custom_delivery_fee: data.price_detail.custom_delivery_fee,
        });
        /* gst comes from settings and total_price from the loaded lines —
           whichever request lands first, the saved copy must not clobber it. */
        const mergePrice = (prev) => ({
          ...data.price_detail,
          gst: prev.price_detail.gst,
          total_price: prev.price_detail.total_price,
        });
        if (orderType === "Retail") {
          setOneTimeDelivery(raw.frequency === "Once");
          setOrderDetail((prev) => ({
            ...prev,
            ...data,
            price_detail: mergePrice(prev),
          }));
          return;
        }
        const director = parseJSON(raw.fd_code, {});
        /* orders from before the feature carry no fd_commission → off */
        const savedCommission = commissionOf(raw);
        setLoadedCommission(savedCommission);
        setApplyFdCommission(savedCommission.apply);
        const datetimeData = { ...data.datetime };
        if (raw.tributes_delivery_time !== undefined) {
          datetimeData.tributes_delivery_time = raw.tributes_delivery_time;
        }
        setCcChoice(
          destChoice(
            raw.casket_cover_delivery_location,
            raw.funeral_location,
            director.cc_delivery_location
          )
        );
        setTriChoice(
          destChoice(
            raw.tribuite_delivery_location,
            raw.funeral_location,
            director.cc_delivery_location
          )
        );
        setOrderDetail((prev) => ({
          ...prev,
          ...data,
          price_detail: mergePrice(prev),
          datetime: datetimeData,
          fd_code: { "FD Code": director },
        }));
      });

    const loadLines =
      orderType === "Retail"
        ? fetchLines("products", "products").then(setSelectedProducts)
        : Promise.all([
            fetchLines("Casket cover", "Casket cover", casketList).then(
              setSelectedProducts
            ),
            fetchLines("tribuites", "Tribute", casketList).then(
              setSelectedTribuites
            ),
          ]);

    return Promise.all([loadOrder, loadLines]).catch(() => {
      setAlert({
        type: "error",
        title: "Could not load the work order",
        message: "The work order could not be loaded. Go back and try again.",
      });
    });
  }

  /* One search per pause in typing. Answers can come back out of order — a
     slow "Mary" landing after "Mary Smith" used to replace the list with the
     wrong one — so each search cancels the one before it and only the latest
     may touch state. */
  function searchFunerals(query) {
    const state = funeralSearch.current;
    if (state.controller) state.controller.abort();
    const controller = new AbortController();
    const seq = (state.seq += 1);
    state.controller = controller;
    const latest = () => seq === state.seq;

    setFuneralsLoading(true);
    axios
      .get(
        BASE_SERVER_URL + "/wo/searchFunerals/" + encodeURIComponent(query),
        { ...bizConfig(), signal: controller.signal }
      )
      .then((doc) => {
        if (latest()) setFunerals(doc.data.response || []);
      })
      .catch((err) => {
        if (latest() && !axios.isCancel(err)) setFunerals([]);
      })
      .finally(() => {
        if (latest()) {
          state.controller = null;
          setFuneralsLoading(false);
        }
      });
  }

  /* ---------- totals ---------- */
  const productsTotal = useMemo(() => {
    const lines =
      orderType === "Funeral"
        ? [...selectedProducts, ...selectedTribuites]
        : [...selectedProducts];
    return lines
      .filter((x) => !x.deleted)
      .reduce((sum, x) => {
        const price = x.customPrice ? x.customPrice : x.price;
        return sum + Number(price * x.amount) + Number(x.sashPrice || 0);
      }, 0);
  }, [selectedProducts, selectedTribuites, orderType]);

  useEffect(() => {
    setProperty({ name: "total_price", value: productsTotal }, "price_detail");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsTotal]);

  const gstRate = Number(orderDetail.price_detail.gst) || 0.1;
  const feeValue =
    deliveryFee.delivery_fee === "Custom Delivery Fee"
      ? Number(deliveryFee.custom_delivery_fee) || 0
      : Number(deliveryFee.delivery_fee) || 0;
  /* Matching components.js PriceTag: the product sum is GST-inclusive, so
     the GST line is the component inside it, and delivery is added on top. */
  const exGst = productsTotal / (1 + gstRate);
  const gstAmount = exGst * gstRate;
  const grandTotal = productsTotal + feeValue;
  /* "None" picked, or Custom with no amount yet */
  const feeUnset =
    !deliveryFee.delivery_fee ||
    (deliveryFee.delivery_fee === "Custom Delivery Fee" && !feeValue);

  /* ---------- funeral director commission ---------- */
  /* The director on the order may be a copy embedded in a linked funeral,
     so the rate comes from the live list where possible. */
  const liveDirector = useMemo(
    () =>
      directors.find(
        (d) => (fd._id && d._id === fd._id) || (fd.code && d.code === fd.code)
      ) || null,
    [directors, fd._id, fd.code]
  );
  const directorRate = Number((liveDirector || fd).commission_pct) || 0;
  /* True when the saved snapshot still belongs to the director now on the
     order — the only case where the snapshotted rate is the one that counts. */
  const snapshotHolds = Boolean(
    loadedCommission &&
      loadedCommission.apply &&
      (loadedCommission.fd_id
        ? loadedCommission.fd_id === fd._id
        : loadedCommission.fd_code === fd.code)
  );
  /* /wo/updateWO leaves commission alone when the flag is absent, which is
     what keeps a snapshotted rate stable across unrelated edits. So the flag
     is only sent when the box changed, or the director under a ticked box
     did — the server then re-resolves the rate from that director. */
  const commissionDirty =
    isEdit &&
    orderType === "Funeral" &&
    (applyFdCommission !== Boolean(loadedCommission && loadedCommission.apply) ||
      (applyFdCommission && !snapshotHolds));
  const commissionPct =
    snapshotHolds && !commissionDirty ? loadedCommission.pct : directorRate;
  const commissionAmount = applyFdCommission
    ? estimateFdCommission(productsTotal, feeValue, commissionPct)
    : 0;
  const commissionHint = (() => {
    if (!fd.code && !fd._id) return "Choose a funeral director first";
    if (applyFdCommission && !liveDirector)
      return "This director isn't saved in this business — saving will be refused";
    if (!applyFdCommission)
      return directorRate > 0
        ? `Director's rate is ${pctLabel(directorRate)} — not applied to this order`
        : "Not applied — this director has no rate set";
    if (snapshotHolds && !commissionDirty) {
      const drift =
        directorRate !== commissionPct
          ? ` · director now ${pctLabel(directorRate)}, this order keeps its rate`
          : "";
      return `Locked at ${pctLabel(commissionPct)} · ≈ ${money(commissionAmount)}${drift}`;
    }
    return `${pctLabel(commissionPct)} of the order total · ≈ ${money(
      commissionAmount
    )}, fixed when you save`;
  })();
  const commissionWarn =
    applyFdCommission && (!liveDirector || directorRate === 0);

  /* Rebuilt on every change so the review overlay and the POST body always
     agree with what the rail is showing. Field-for-field the object the old
     page assembled in its `orderDetailJSON` state. */
  const orderDetailJSON = useMemo(() => {
    if (orderType === "Retail") {
      /* Field-for-field the object RecurringInputPage assembles. */
      const detail = {
        order: pickFields(orderDetail.order, RETAIL_ORDER_FIELDS),
        price_detail: orderDetail.price_detail,
        /* authority to leave is standard now — always granted */
        recipient: { ...orderDetail.recipient, autority_to_leave: true },
        selectedProducts,
      };
      const rate = Number(orderDetail.price_detail.gst) || 0.1;
      return {
        type: "Retail",
        received_by: JSON.stringify(currentUser),
        add_ons: JSON.stringify(""),
        orderDetail: JSON.stringify(detail),
        selectedProducts: JSON.stringify(selectedProducts),
        price_detail: { ...orderDetail.price_detail },
        totalPrice: (productsTotal * (1 + rate)).toFixed(2),
      };
    }
    const detail = {
      order: pickFields(orderDetail.order, FUNERAL_ORDER_FIELDS),
      price_detail: orderDetail.price_detail,
      datetime: orderDetail.datetime,
      fd_code: orderDetail.fd_code,
      selectedProducts,
      selectedTribuites,
      totalPrice: productsTotal,
    };
    return {
      type: "Funeral",
      received_by: JSON.stringify(currentUser),
      orderDetail: JSON.stringify(detail),
      selectedProducts: JSON.stringify(selectedProducts),
      selectedTribuites: JSON.stringify(selectedTribuites),
      selectedParentOrderID: selectedParentOrderID,
      /* top-level sibling of orderDetail, never inside it; Funeral only */
      apply_fd_commission: applyFdCommission,
      totalPrice: productsTotal,
      totalPriceGST: (productsTotal * (1 + 0.1)).toFixed(2),
      price_detail: { ...orderDetail.price_detail },
    };
  }, [
    orderType,
    orderDetail,
    selectedProducts,
    selectedTribuites,
    productsTotal,
    selectedParentOrderID,
    currentUser,
    applyFdCommission,
  ]);


  /* ---------- destination addresses ---------- */
  useEffect(() => {
    if (!ccChoice || ccChoice === "Other address") return;
    const value =
      ccChoice === "Funeral director"
        ? fd.cc_delivery_location || ""
        : datetime.funeral_location || "";
    setProperty(
      { name: "casket_cover_delivery_location", value },
      "datetime"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccChoice, fd.cc_delivery_location, datetime.funeral_location]);

  useEffect(() => {
    if (!triChoice || triChoice === "Other address") return;
    const value =
      triChoice === "Funeral director"
        ? fd.cc_delivery_location || ""
        : datetime.funeral_location || "";
    setProperty({ name: "tribuite_delivery_location", value }, "datetime");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triChoice, fd.cc_delivery_location, datetime.funeral_location]);

  /* ---------- picker data ---------- */
  const directorItems = useMemo(
    () =>
      directors.map((d) => ({
        id: d._id || d.code,
        chip: (d.code || "?").slice(0, 3).toUpperCase(),
        primary: d.business_name || d.code,
        secondary: [locationName(d.address), d.contact_number]
          .filter(Boolean)
          .join(" · "),
        meta: d.code || "",
        group: "all",
        raw: d,
      })),
    [directors]
  );

  /* /wo/searchFunerals returns work orders, so several rows can belong to the
     same funeral. The count is what powers the duplicate warning. */
  const funeralRecords = useMemo(() => {
    const counts = {};
    funerals.forEach((f) => {
      const key = (f.late_person_name || "").toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
    });
    const seen = new Set();
    return funerals
      .filter((f) => {
        const key = (f.late_person_name || "").toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((f) => {
        const director = parseJSON(f.fd_code, {});
        const days = daysFromToday(f.funeral_date);
        return {
          id: f._id,
          serial: f.serial_no || f._id,
          name: f.late_person_name,
          fd: director.business_name
            ? `${director.business_name} (${director.code})`
            : director.code || "—",
          service: [f.funeral_date, f.funeral_time].filter(Boolean).join(" · "),
          venue: locationName(f.funeral_location),
          linked: counts[(f.late_person_name || "").toLowerCase()] || 0,
          status: days !== null && days < 0 ? "Past funeral" : "Open funeral",
          upcoming: days !== null && days >= 0 && days <= 7,
          raw: f,
        };
      });
  }, [funerals]);

  const personItems = useMemo(
    () =>
      funeralRecords.map((r) => ({
        id: r.id,
        chip: initials(r.name),
        primary: r.name,
        secondary: [r.fd, r.service].filter(Boolean).join(" · "),
        meta: r.linked ? `${r.linked} linked` : "",
        group: r.upcoming ? "open" : "other",
      })),
    [funeralRecords]
  );


  /* ---------- selection handlers ---------- */

  /* The live profile, not the copy saved on an older order, so a linked
     funeral shows today's instructions. */
  function announceDirector(director) {
    const d = director || {};
    const current =
      directors.find(
        (x) => (d._id && x._id === d._id) || (d.code && x.code === d.code)
      ) || d;
    const note = directorNote(current);
    if (note) {
      setFdNote({
        name: current.business_name || current.code || "Funeral director",
        note,
      });
    }
  }

  /* Picking an existing funeral links this order to it, the way the old
     page's ParentOrderSearchResultList did: the service details come from the
     parent and are locked so two orders can't disagree. */
  function selectFuneral(id) {
    const record = funeralRecords.find((r) => r.id === id);
    if (!record) return;
    const raw = record.raw;
    setSelectedParentOrder(record);
    setSelectedParentOrderID(raw._id);
    setOrderDetail((prev) => ({
      ...prev,
      order: { ...prev.order, late_person_name: raw.late_person_name },
      datetime: {
        ...prev.datetime,
        funeral_date: raw.funeral_date,
        funeral_time: raw.funeral_time,
        funeral_location: raw.funeral_location,
      },
      fd_code: { "FD Code": parseJSON(raw.fd_code, {}) },
    }));
    setPicker(null);
    announceDirector(parseJSON(raw.fd_code, {}));
  }

  function clearFuneral() {
    setSelectedParentOrder(null);
    setSelectedParentOrderID("");
    setProperty({ name: "late_person_name", value: "" }, "order");
  }

  function createDirector({ values, kind }) {
    const payload = {
      business_name: values.business_name || "",
      code: values.code || "",
      contact_number: values.contact_number || "",
      address: values.suburb ? JSON.stringify({ name: values.suburb }) : "",
      cc_delivery_location: "",
      email_address: "",
      owner_name: "",
      manager_name: "",
      unique_info: kind || "",
    };
    return axios
      .post(BASE_SERVER_URL + "/fd/addNew", payload, bizConfig())
      .then(() => axios.get(BASE_SERVER_URL + "/fd/getAll", bizConfig()))
      .then((doc) => {
        const list = doc.data.response || [];
        setDirectors(list);
        const created =
          list.find((d) => d.code === payload.code) || list[list.length - 1];
        if (created) setProperty({ name: "FD Code", value: created }, "fd_code");
        setPicker(null);
      })
      .catch((err) => {
        setAlert({
          type: "error",
          title: "Could not save the director",
          message:
            "The funeral director was not saved. Check the code is unique and try again.",
        });
        throw err;
      });
  }

  function createPerson({ values, query }) {
    const name = values.late_person_name || query;
    if (!name) return;
    setSelectedParentOrder(null);
    setSelectedParentOrderID("");
    setProperty({ name: "late_person_name", value: name }, "order");
    setPicker(null);
  }

  /* A funeral line remembers which half it was added in: a type filed
     under the casket cover in Settings (singles, petals…) put in the
     Tributes half is a tribute everywhere — list, day sheet, run sheet,
     counts, and this form when it is reopened (lineGoesWithCasket). */
  function stampHalf(line, group) {
    if (orderType !== "Funeral") return line;
    return { ...line, as_tribute: group === "Tributes" };
  }

  function addLine(picked) {
    const stamped = stampHalf(picked, modalGroup);
    const line = isEdit ? { ...stamped, newPrdt: true } : stamped;
    if (orderType === "Funeral" && modalGroup === "Tributes") {
      setSelectedTribuites((prev) => [...prev, line]);
    } else {
      setSelectedProducts((prev) => [...prev, line]);
    }
  }

  /* Editing a rail line. A saved line that keeps its product is updated in
     place (`editProduct` — /wo/updateWO $sets it by its line _id); one
     switched to another product is deleted and the new one added after it,
     the same split the legacy editor made. Unsaved lines are just replaced. */
  function updateLine(picked) {
    if (!editingLine) return;
    const { group, index } = editingLine;
    const line = stampHalf(picked, group);
    const setter =
      group === "Tributes" ? setSelectedTribuites : setSelectedProducts;
    setter((prev) => {
      const old = prev[index];
      if (!old) return prev;
      if (old.fromDB) {
        if (line._id === old._id) {
          return prev.map((l, i) =>
            i === index ? { ...line, editProduct: true } : l
          );
        }
        const next = prev.map((l, i) =>
          i === index ? { ...l, deleted: true, editProduct: false } : l
        );
        next.splice(index + 1, 0, { ...line, newPrdt: true });
        return next;
      }
      return prev.map((l, i) =>
        i === index ? (old.newPrdt ? { ...line, newPrdt: true } : line) : l
      );
    });
    setEditingLine(null);
  }

  /* A saved line can't just vanish from the array — /wo/updateWO deletes the
     ones that come back flagged `deleted`. */
  function removeLine(group, index) {
    const setter =
      group === "Tributes" ? setSelectedTribuites : setSelectedProducts;
    setter((prev) =>
      prev[index] && prev[index].fromDB
        ? prev.map((l, i) =>
            i === index ? { ...l, deleted: true, editProduct: false } : l
          )
        : prev.filter((_, i) => i !== index)
    );
  }

  /* ---------- retail schedule (mirrors RecurringInputPage) ---------- */
  function toggleOneTime() {
    setOneTimeDelivery((prev) => {
      setProperty(
        { name: "frequency", value: !prev ? "Once" : "" },
        "recipient"
      );
      setProperty({ name: "delivery_day", value: "" }, "recipient");
      setProperty({ name: "delivery_date", value: "" }, "recipient");
      return !prev;
    });
  }

  /* Picking a weekday sets the start date to its next occurrence; picking a
     start date sets the weekday — the same pairing the old page kept. */
  function chooseDeliveryDay(index) {
    setProperty({ name: "delivery_day", value: index }, "recipient");
    const today = new Date();
    let add = index - today.getDay();
    if (add <= 0) add += 7;
    const next = new Date(today);
    next.setDate(today.getDate() + add);
    const dd = String(next.getDate()).padStart(2, "0");
    const mm = String(next.getMonth() + 1).padStart(2, "0");
    setProperty(
      {
        name: "order_start_date",
        value: `${dd}/${mm}/${next.getFullYear()}`,
      },
      "recipient"
    );
  }

  function chooseOrderStartDate(value) {
    setProperty({ name: "order_start_date", value }, "recipient");
    const iso = toISODate(value);
    if (iso) {
      const day = new Date(`${iso}T00:00:00`).getDay();
      if (!isNaN(day)) {
        setProperty({ name: "delivery_day", value: day }, "recipient");
      }
    }
  }

  /* ---------- validation + submit (same rules as FuneralInputPage) ---------- */
  function liveLines(json) {
    return JSON.parse(json || "[]").filter((l) => !l.deleted);
  }

  function validateData(data) {
    const detail = JSON.parse(data.orderDetail);
    if (orderType === "Retail") {
      const recip = detail.recipient || {};
      if (
        recip.frequency !== "Once" &&
        (!recip.frequency || (!recip.delivery_day && recip.delivery_day !== 0))
      ) {
        return "Please provide delivery day and frequency for the Work Order";
      }
      if (recip.frequency === "Once" && !recip.delivery_date) {
        return "Please provide delivery date for the Work Order";
      }
      if (!detail.order.payment_option) {
        return "Please select payment method";
      }
      if (liveLines(data.selectedProducts).length === 0) {
        return "Please select at least one product";
      }
      return "valid";
    }
    if (!detail.order.late_person_name) {
      return "Please enter the late person's name";
    }
    if (!detail.order.payment_option) {
      return "Please select payment method";
    }
    if (!detail.datetime.funeral_date) {
      return "Please enter funeral date";
    }
    const ccLines = liveLines(data.selectedProducts);
    const triLines = liveLines(data.selectedTribuites);
    if (ccLines.length === 0 && triLines.length === 0) {
      return "Please select at least one product";
    }
    /* the run sheet needs a place and a time for every delivery */
    const needs = destinationsNeeded(
      ccLines.concat(triLines),
      casketTypesOf(settings)
    );
    const dt = detail.datetime || {};
    const blank = (v) => !String(v || "").trim();
    if (needs.cc && !locationName(dt.casket_cover_delivery_location)) {
      return "Please choose where the casket cover goes";
    }
    if (needs.cc && blank(dt.casket_cover_delivery_time)) {
      return "Please enter the casket cover delivery time";
    }
    if (needs.tributes && !locationName(dt.tribuite_delivery_location)) {
      return "Please choose where the tributes go";
    }
    if (needs.tributes && blank(dt.tributes_delivery_time)) {
      return "Please enter the tributes delivery time";
    }
    return "valid";
  }

  function isOrderValid() {
    const result = validateData(orderDetailJSON);
    if (result !== "valid") {
      setAlert({
        title: "Incomplete Work Order Data Submission",
        message: "Incomplete Data Submition : " + result,
      });
      return false;
    }
    return true;
  }

  /* Field-for-field the `updateData` the legacy edit pages post. */
  function updatePayload() {
    if (orderType === "Retail") {
      return {
        id,
        updateData: {
          price_detail: { ...orderDetail.price_detail },
          ...orderDetail.order,
          ...orderDetail.recipient,
          products_selected: JSON.stringify(selectedProducts),
        },
      };
    }
    const payload = {
      id,
      updateData: {
        _id: orderDetail.order._id,
        type: orderType,
        price_detail: { ...orderDetail.price_detail },
        ...orderDetail.order,
        ...orderDetail.datetime,
        fd_code: JSON.stringify(orderDetail.fd_code["FD Code"] || {}),
        selectedProducts: JSON.stringify(selectedProducts),
        selectedTribuites: JSON.stringify(selectedTribuites),
      },
    };
    /* beside updateData, not inside it — absent means "leave as is" */
    if (commissionDirty) payload.apply_fd_commission = applyFdCommission;
    return payload;
  }

  function submitHandler() {
    setLoading(true);
    const request = isEdit
      ? axios.post(BASE_SERVER_URL + "/wo/updateWO", updatePayload())
      : axios.post(BASE_SERVER_URL + "/wo/addNew", orderDetailJSON, bizConfig());
    request
      .then((response) => {
        if (response.data.status === "success") {
          toast.success(
            isEdit
              ? "Work Order Updated"
              : inOtherBusiness
              ? `New Work Order Added to ${targetLabel}`
              : "New Work Order Added",
            {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "light",
          });
          /* Back to "All" — except a retail order at a retail-first
             business (The Floral Decorator), which returns to Retail. */
          navigate(
            orderType === "Retail" &&
              defaultOrderTypeFor(activeBusiness) === "Retail"
              ? "/WorkOrderList/retail"
              : "/WorkOrderList"
          );
        } else {
          /* a refused commission comes back as { status: "failed", message } */
          toast.error(
            "Error : " + (response.data.message || response.data.error),
            {
              position: "top-center",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              theme: "light",
            }
          );
        }
      })
      .catch(() => {
        /* stay on the review with the draft intact — no navigation */
        toast.error("Could not save the work order", {
          position: "top-center",
          autoClose: 3000,
          theme: "light",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }

  if (pageLoading) {
    return <PageLoading noLogo={true} spinning={true} />;
  }

  const lockedByParent = Boolean(selectedParentOrderID);
  const isRetail = orderType === "Retail";
  /* Lines flagged `deleted` stay in state for /wo/updateWO but are gone as
     far as the screen is concerned. */
  const liveProducts = selectedProducts.filter((l) => !l.deleted);
  const liveTribuites = selectedTribuites.filter((l) => !l.deleted);
  const destNeeds = destinationsNeeded(
    liveProducts.concat(liveTribuites),
    casketTypesOf(settings)
  );
  /* The casket cover half offers the Casket cover type plus what Settings
     files with it. The tributes half offers everything but the casket cover
     itself — singles, petals and the other filed types included, since a
     family sometimes wants them as tributes; a line added there is stamped
     `as_tribute` (stampHalf) and stays a tribute on every screen. */
  const casketList = [
    "Casket cover",
    ...casketTypesOf(settings).filter((t) => !isCasketCover(t)),
  ];
  const pickerGroup =
    (editingLine && editingLine.group) || modalGroup || "Tributes";
  const pickerTypeRule = isRetail
    ? null
    : pickerGroup === "Casket cover"
    ? { only: casketList }
    : { except: ["Casket cover"] };
  const hasLines =
    (isRetail
      ? liveProducts.length
      : liveProducts.length + liveTribuites.length) > 0;
  const detailsStarted = isRetail
    ? Boolean(order.company_name || recipient.recipient_name)
    : Boolean(order.late_person_name);
  const listPath = isRetail ? "/WorkOrderList/retail" : "/WorkOrderList";
  const railName =
    (isRetail
      ? order.company_name || recipient.recipient_name
      : order.late_person_name) || (isEdit ? "Work order" : "New order");
  const railPlace = isRetail
    ? locationName(recipient.delivery_location)
    : locationName(datetime.funeral_location);
  const feeOptions = (settings.delivery_fee || "")
    .split(",")
    .map((each) => Number(each))
    .filter((each) => !isNaN(each) && each !== 0)
    .map((each) => each.toFixed(2));
  /* a saved fee that has since left the settings list still has to show */
  if (
    deliveryFee.delivery_fee &&
    deliveryFee.delivery_fee !== "Custom Delivery Fee" &&
    !feeOptions.includes(deliveryFee.delivery_fee)
  ) {
    feeOptions.push(deliveryFee.delivery_fee);
  }

  const groups = isRetail
    ? [{ name: "Flower arrangements", items: selectedProducts }]
    : [
        { name: "Casket cover", items: selectedProducts },
        { name: "Tributes", items: selectedTribuites },
      ];

  const directorLabel = fd.business_name
    ? `${fd.business_name}${fd.code ? ` — ${fd.code}` : ""}`
    : fd.code || "";

  if (showSummary) {
    return (
      <div className="rf-neworder">
        {Object.keys(alert).length > 0 && (
          <PopUpAlert
            info={{ ...alert, blurBackground: true }}
            onClose={() => setAlert({})}
          />
        )}
        <ReviewOrder
          createIn={inOtherBusiness ? targetLabel : ""}
          orderType={orderType}
          orderDetail={orderDetail}
          selectedProducts={liveProducts}
          selectedTribuites={liveTribuites}
          deliveryFee={deliveryFee}
          currentUser={currentUser}
          totals={{
            productsTotal,
            exGst,
            gstAmount,
            gstRate,
            feeValue,
            grandTotal,
          }}
          commission={
            orderType === "Funeral"
              ? { apply: applyFdCommission, pct: commissionPct, amount: commissionAmount }
              : null
          }
          busy={loading}
          isEdit={isEdit}
          onBack={() => setShowSummary(false)}
          onConfirm={submitHandler}
        />
      </div>
    );
  }

  return (
    <div className="rf-neworder">
      {Object.keys(alert).length > 0 && (
        <PopUpAlert
          info={{ ...alert, blurBackground: true }}
          onClose={() => setAlert({})}
        />
      )}

      {fdNote && (
        <div className="rf-fdnote-scrim" onClick={() => setFdNote(null)}>
          <div
            className="rf-fdnote"
            role="alertdialog"
            aria-labelledby="rf-fdnote-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rf-fdnote-eyebrow">Special instructions</div>
            <div id="rf-fdnote-title" className="rf-serif rf-fdnote-title">
              {fdNote.name}
            </div>
            <div className="rf-fdnote-body">{fdNote.note}</div>
            <div className="rf-fdnote-actions">
              <button
                type="button"
                className="rf-btn rf-btn-primary"
                autoFocus
                onClick={() => setFdNote(null)}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- step bar ---------- */}
      <div className="rf-steps">
        <Step
          n={1}
          label="Details"
          state={detailsStarted ? "done" : "active"}
          onClick={() => {}}
        />
        <Step
          n={2}
          label="Products"
          state={hasLines ? "done" : detailsStarted ? "active" : "todo"}
          onClick={() =>
            setModalGroup(isRetail ? "Flower arrangements" : "Tributes")
          }
        />
        <Step
          n={3}
          label="Review"
          state="todo"
          onClick={() => {
            if (isOrderValid()) setShowSummary(true);
          }}
        />
        <span className="rf-spacer" />
        <span className="rf-steps-meta">
          {isEdit
            ? `Editing${
                (orderDetail.ids || {}).serial_no
                  ? ` #${orderDetail.ids.serial_no}`
                  : ""
              } · changes not saved yet`
            : "Nothing saved yet"}{" "}
          ·{" "}
          <button
            type="button"
            className="rf-link"
            onClick={() => navigate(listPath)}
          >
            Discard
          </button>
        </span>
      </div>

      <div className="rf-neworder-body">
        {/* ---------- form ---------- */}
        <div className="rf-form">
          <div className="rf-form-bar">
            <div className="rf-segmented">
              {/* an existing order can't change type */}
              {(isEdit ? [orderType] : ["Funeral", "Retail"]).map((k) => (
                <button
                  key={k}
                  type="button"
                  className={k === orderType ? "is-active" : ""}
                  onClick={() => {
                    setOrderType(k);
                    setTypeTouched(true);
                    setPicker(null);
                    setModalGroup(null);
                  }}
                >
                  {k}
                </button>
              ))}
            </div>
            <span className="rf-form-bar-note">
              {isEdit
                ? "Editing an existing work order"
                : "Choosing a type changes the fields below"}
            </span>
            {createInOptions.length > 1 && (
              <div className="rf-form-bar-business">
                <span className="rf-label">Create in</span>
                <div className="rf-input-shell">
                  <SelectMenu
                    value={targetBusinessId}
                    options={createInOptions}
                    onChange={chooseBusiness}
                  />
                </div>
              </div>
            )}
          </div>
          {inOtherBusiness && (
            <div className="rf-business-note">
              This order will be created in <b>{targetLabel}</b>, not{" "}
              {(activeBusiness && activeBusiness.name) || "the business you are in"}.
              Its directors, products and prices are the ones shown here.
            </div>
          )}

          {/* order & payment */}
          <section className="rf-card">
            <div className="rf-card-head">
              <span className="rf-card-title">Order &amp; payment</span>
              <span className="rf-card-meta">
                {isRetail ? "6 fields · 1 required" : "7 fields · 3 required"}
              </span>
            </div>

            {/* late person's name — search, then a pinned record card */}
            {/* editing keeps the order on its funeral — /wo/updateWO never
                re-links the parent — so the name is a plain field */}
            {!isRetail && isEdit && (
              <Field
                label="Late person’s name"
                required
                value={order.late_person_name}
                onChange={(v) =>
                  setProperty({ name: "late_person_name", value: v }, "order")
                }
              />
            )}
            {!isRetail && !isEdit && (
            <div className="rf-person-block">
              <label className="rf-label">
                Late person&rsquo;s name<span className="rf-req">•</span>
              </label>

              {!selectedParentOrder && (
                <div
                  className={`rf-input-shell is-picker${
                    picker === "person" ? " is-open" : ""
                  }`}
                  onClick={() =>
                    setPicker(picker === "person" ? null : "person")
                  }
                >
                  <span className="rf-ss-icon" />
                  {order.late_person_name ? (
                    <span className="rf-picker-value">
                      {order.late_person_name}
                    </span>
                  ) : (
                    <span className="rf-picker-placeholder">
                      Search an existing funeral, or type a new name
                    </span>
                  )}
                  <span className="rf-affordance">
                    {funeralRecords.length} records
                  </span>
                </div>
              )}

              {selectedParentOrder && (
                <PersonCard
                  record={selectedParentOrder}
                  onClear={clearFuneral}
                  onChange={() => setPicker("person")}
                />
              )}

              {picker === "person" && (
                <div className="rf-picker-panel is-down rf-picker-wide">
                  <SearchSelect
                    items={personItems}
                    selectedId={selectedParentOrderID}
                    chipTone="sand"
                    loading={funeralsLoading}
                    onQuery={searchFunerals}
                    placeholder="Search by name, or type a new one"
                    groups={[
                      {
                        key: "open",
                        label: "Open funerals this week",
                        queryLabel: "Best matches",
                      },
                      {
                        key: "other",
                        label: "Other records",
                        queryLabel: "Other records",
                      },
                    ]}
                    onSelect={selectFuneral}
                    onClose={() => setPicker(null)}
                    add={{
                      /* the typed name is the record — no second form */
                      direct: true,
                      label: (q) =>
                        q
                          ? `New funeral “${q}”`
                          : "New funeral — type the late person's name above",
                    }}
                    onCreate={createPerson}
                  />
                </div>
              )}
            </div>
            )}

            <div className="rf-grid-2">
              {isRetail && (
                <Field
                  label="Company name"
                  value={order.company_name}
                  placeholder="Who the order is billed to"
                  onChange={(v) =>
                    setProperty({ name: "company_name", value: v }, "order")
                  }
                />
              )}

              {!isRetail && (
              <PickerField
                label="Funeral director"
                required
                value={directorLabel}
                placeholder="Choose a funeral director"
                hint={
                  fd.unique_info ||
                  (lockedByParent
                    ? "Set by the linked funeral"
                    : "Saved directors carry their own delivery address")
                }
                open={picker === "fd"}
                onOpen={() => {
                  if (lockedByParent) return;
                  setPicker(picker === "fd" ? null : "fd");
                }}
              >
                <SearchSelect
                  items={directorItems}
                  selectedId={fd._id || fd.code}
                  chipTone="gold"
                  placeholder="Search directors by name or code"
                  groups={[
                    {
                      key: "all",
                      label: "All directors",
                      queryLabel: "Best matches",
                    },
                  ]}
                  onSelect={(id, item) => {
                    setProperty({ name: "FD Code", value: item.raw }, "fd_code");
                    setPicker(null);
                    announceDirector(item.raw);
                  }}
                  onClose={() => setPicker(null)}
                  add={{
                    label: "Add a new funeral director",
                    title: "New funeral director",
                    saveLabel: "Save director",
                    note:
                      "Saved directors appear in this list for every future order. Their casket-cover delivery address is set on the Funeral directors page.",
                    fields: [
                      {
                        name: "business_name",
                        label: "Director name",
                        placeholder: "e.g. Harrison Funerals",
                        span: "span 2",
                      },
                      { name: "code", label: "Code", placeholder: "HAR" },
                      {
                        name: "contact_number",
                        label: "Phone",
                        placeholder: "03 5261 3300",
                      },
                      {
                        name: "suburb",
                        label: "Suburb",
                        placeholder: "Torquay",
                        span: "span 2",
                      },
                    ],
                    kindLabel: "Billing",
                    kinds: ["Account customer", "Pay per order"],
                  }}
                  onCreate={createDirector}
                />
              </PickerField>
              )}

              {/* Funeral only — a Retail order can never be commissioned and
                  the server ignores the flag there anyway. */}
              {!isRetail && (
                <div className="rf-field rf-fd-commission">
                  {/* label / control / hint, on the picker's own rhythm so the
                      box sits level with the director input beside it */}
                  <span className="rf-label">Commission</span>
                  <label className="rf-check">
                    <input
                      type="checkbox"
                      checked={applyFdCommission}
                      onChange={(e) => setApplyFdCommission(e.target.checked)}
                    />
                    <span className="rf-check-box" aria-hidden="true">
                      ✓
                    </span>
                    <span className="rf-check-label">
                      Apply funeral director commission
                    </span>
                  </label>
                  <span className={`rf-hint${commissionWarn ? " is-warn" : ""}`}>
                    {commissionHint}
                  </span>
                </div>
              )}

              <Field
                label="Ordered by"
                value={order.ordered_by}
                placeholder="Who placed the order"
                onChange={(v) =>
                  setProperty({ name: "ordered_by", value: v }, "order")
                }
              />

              <Field
                label="Contact phone"
                value={order.orderer_phone}
                placeholder="03 9844 2100"
                onChange={(v) =>
                  setProperty({ name: "orderer_phone", value: v }, "order")
                }
              />

              <Field label="Payment method" required affordance="▾">
                {/* UNPAID reads red and bold, as it did on the old form,
                    both in the list and once chosen */}
                <select
                  className={order.payment_option === "**UNPAID**" ? "is-unpaid" : ""}
                  value={order.payment_option || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setProperty({ name: "payment_option", value }, "order");
                    if (value === "Eftpos/Credit Card") {
                      setProperty(
                        { name: "payment_email", value: "" },
                        "order"
                      );
                    } else {
                      setProperty({ name: "payment_date", value: "" }, "order");
                    }
                  }}
                >
                  <option value="">Choose a payment method</option>
                  {(isRetail ? RETAIL_PAYMENT_OPTIONS : PAYMENT_OPTIONS).map((o) => (
                    <option
                      key={o}
                      value={o}
                      className={o === "**UNPAID**" ? "is-unpaid" : ""}
                    >
                      {o}
                    </option>
                  ))}
                </select>
              </Field>

              {order.payment_option === "Eftpos/Credit Card" && (
                <Field label="Payment date">
                  <DateField
                    value={order.payment_date}
                    onChange={(v) =>
                      setProperty({ name: "payment_date", value: v }, "order")
                    }
                  />
                </Field>
              )}

              {(isRetail ? RETAIL_EMAIL_PAYMENTS : EMAIL_PAYMENTS).indexOf(
                order.payment_option
              ) !== -1 && (
                <Field
                  label="Invoice email"
                  type="email"
                  value={order.payment_email}
                  placeholder="accounts@example.com"
                  onChange={(v) =>
                    setProperty({ name: "payment_email", value: v }, "order")
                  }
                />
              )}

              <Field
                label="Invoice number"
                value={order.invoice_number}
                placeholder="Add invoice number"
                hint="Leave blank to mark the order UNPAID"
                hintTone="warn"
                onChange={(v) =>
                  setProperty({ name: "invoice_number", value: v }, "order")
                }
              />
            </div>
          </section>

          {/* service, times & delivery */}
          {!isRetail && (
          <section className="rf-card rf-card-grow">
            <div className="rf-card-head">
              <span className="rf-card-title">Service, times &amp; delivery</span>
              <span className="rf-card-meta">Times drive the run sheets</span>
            </div>

            <div className="rf-grid-3">
              <Field label="Funeral date" required>
                <DateField
                  disabled={lockedByParent}
                  value={datetime.funeral_date}
                  onChange={(v) =>
                    setProperty({ name: "funeral_date", value: v }, "datetime")
                  }
                />
              </Field>

              {/* free text, like the old form: the shop writes "11.30am",
                  "11.30AM DROP OFF" and the like, which a time control
                  would refuse */}
              <Field label="Funeral time" required>
                <input
                  disabled={lockedByParent}
                  value={datetime.funeral_time || ""}
                  placeholder="e.g. 11.30am"
                  onChange={(e) =>
                    setProperty(
                      { name: "funeral_time", value: e.target.value },
                      "datetime"
                    )
                  }
                />
              </Field>

              {/* Google address search, like the retail delivery address —
                  no saved director / venue list: the shop types the church,
                  chapel or street every time. Venue names work too (Google
                  knows "Wesley Uniting Church"), and a typed address that
                  Google can't match still saves as is. */}
              <Field label="Funeral location" required span="span 2">
                <AddressAutocomplete
                  disabled={lockedByParent}
                  value={locationName(datetime.funeral_location)}
                  placeholder="Search a venue or street address"
                  onChange={(json) =>
                    setProperty({ name: "funeral_location", value: json }, "datetime")
                  }
                />
              </Field>
            </div>

            <div className="rf-grid-2">
              <DestinationCard
                title="Casket cover goes to"
                required={destNeeds.cc}
                choice={ccChoice}
                onChoice={setCcChoice}
                time={datetime.casket_cover_delivery_time}
                onTime={(v) =>
                  setProperty(
                    { name: "casket_cover_delivery_time", value: v },
                    "datetime"
                  )
                }
                address={locationName(datetime.casket_cover_delivery_location)}
                onAddress={(json) =>
                  setProperty(
                    { name: "casket_cover_delivery_location", value: json },
                    "datetime"
                  )
                }
              />
              <DestinationCard
                title="Tributes go to"
                required={destNeeds.tributes}
                choice={triChoice}
                onChoice={setTriChoice}
                time={datetime.tributes_delivery_time}
                onTime={(v) =>
                  setProperty(
                    { name: "tributes_delivery_time", value: v },
                    "datetime"
                  )
                }
                address={locationName(datetime.tribuite_delivery_location)}
                onAddress={(json) =>
                  setProperty(
                    { name: "tribuite_delivery_location", value: json },
                    "datetime"
                  )
                }
              />
            </div>
          </section>
          )}

          {/* recipient & delivery — the retail half of the toggle. The same
             fields RecurringInputPage collects, in the redesign's clothes. */}
          {isRetail && (
          <section className="rf-card rf-card-grow">
            <div className="rf-card-head">
              <span className="rf-card-title">Recipient &amp; delivery</span>
              <span className="rf-card-meta">
                Delivery drives the run sheets
              </span>
            </div>

            <div className="rf-grid-2">
              <Field
                label="Recipient's name"
                value={recipient.recipient_name}
                placeholder="Who receives the flowers"
                onChange={(v) =>
                  setProperty({ name: "recipient_name", value: v }, "recipient")
                }
              />
              <Field
                label="Recipient's phone"
                value={recipient.recipient_phone}
                placeholder="0400 000 000"
                onChange={(v) =>
                  setProperty(
                    { name: "recipient_phone", value: v },
                    "recipient"
                  )
                }
              />
              <Field label="Delivery location" span="span 2">
                <AddressAutocomplete
                  value={locationName(recipient.delivery_location)}
                  placeholder="Start typing a street address"
                  onChange={(json) =>
                    setProperty(
                      { name: "delivery_location", value: json },
                      "recipient"
                    )
                  }
                />
              </Field>
              <Field label="Delivery area">
                <SelectMenu
                  value={recipient.delivery_area || ""}
                  options={DELIVERY_AREAS}
                  placeholder="Choose an area"
                  onChange={(v) =>
                    setProperty({ name: "delivery_area", value: v }, "recipient")
                  }
                />
              </Field>
              <Field
                label="Delivery time"
                value={recipient.time_of_delivery}
                placeholder="e.g. before 2pm"
                onChange={(v) =>
                  setProperty(
                    { name: "time_of_delivery", value: v },
                    "recipient"
                  )
                }
              />
            </div>

            {/* schedule: a single dated delivery by default, or repeating */}
            <div className="rf-retail-sched">
              <label className="rf-check">
                <input
                  type="checkbox"
                  checked={oneTimeDelivery}
                  onChange={toggleOneTime}
                />
                <span className="rf-check-box" aria-hidden="true">
                  ✓
                </span>
                <span className="rf-check-label">One-time delivery</span>
                <span className="rf-check-meta">
                  {oneTimeDelivery
                    ? "Delivered once on the chosen date"
                    : "Repeats until the order is cancelled"}
                </span>
              </label>
            </div>

            {oneTimeDelivery ? (
              <div className="rf-grid-3">
                <Field label="Delivery date" required>
                  <DateField
                    value={recipient.delivery_date}
                    onChange={(v) =>
                      setProperty({ name: "delivery_date", value: v }, "recipient")
                    }
                  />
                </Field>
              </div>
            ) : (
              <div className="rf-grid-3">
                <Field label="Frequency" required affordance="▾">
                  <select
                    value={recipient.frequency || ""}
                    onChange={(e) =>
                      setProperty(
                        { name: "frequency", value: e.target.value },
                        "recipient"
                      )
                    }
                  >
                    <option value="">Choose frequency</option>
                    {FREQUENCIES.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Delivery day" required affordance="▾">
                  <select
                    value={
                      recipient.delivery_day === "" ||
                      recipient.delivery_day === undefined
                        ? ""
                        : String(recipient.delivery_day)
                    }
                    onChange={(e) =>
                      chooseDeliveryDay(Number(e.target.value))
                    }
                  >
                    <option value="">Choose a day</option>
                    {weekDays.map((d, i) => (
                      <option key={d} value={i}>
                        {d}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Order start date">
                  <DateField
                    value={recipient.order_start_date}
                    onChange={chooseOrderStartDate}
                  />
                </Field>
              </div>
            )}

            <div className="rf-grid-2">
              <Field label="Card message" span="span 2" area>
                <textarea
                  rows={2}
                  value={order.card_message || ""}
                  placeholder="The message printed on the card"
                  onChange={(e) =>
                    setProperty(
                      { name: "card_message", value: e.target.value },
                      "order"
                    )
                  }
                />
              </Field>
              <Field label="Comment" span="span 2" area>
                <textarea
                  rows={2}
                  value={order.order_comment || ""}
                  placeholder="Anything the bench should know"
                  onChange={(e) =>
                    setProperty(
                      { name: "order_comment", value: e.target.value },
                      "order"
                    )
                  }
                />
              </Field>
            </div>
            {/* No "Authority to leave" box — it is standard for every retail
                delivery now, so the payload always sends it as granted. */}
          </section>
          )}
        </div>

        {/* ---------- live summary rail ---------- */}
        <aside className="rf-rail">
          <div className="rf-rail-head">
            <div className="rf-label">This order</div>
            <div className="rf-serif rf-rail-name">{railName}</div>
            <div className="rf-rail-meta">
              {orderType}
              {railPlace ? ` · ${railPlace}` : ""}
            </div>
            {inOtherBusiness && (
              <div className="rf-rail-business">In {targetLabel}</div>
            )}
          </div>

          {/* Delivery fee — up here on request: tucked into the totals it was
              easy to miss, and an order would go out on "None". The totals
              row at the bottom only mirrors this. */}
          <div className="rf-field rf-rail-fee-block">
            <label className="rf-label">Delivery fee</label>
            <div className="rf-input-shell">
              <SelectMenu
                value={deliveryFee.delivery_fee || ""}
                options={[
                  { value: "", label: "None" },
                  ...feeOptions.map((o) => ({ value: o, label: `$${o}` })),
                  { value: "Custom Delivery Fee", label: "Custom amount" },
                ]}
                onChange={(v) =>
                  setDeliveryFee((prev) => ({ ...prev, delivery_fee: v }))
                }
              />
            </div>
            {deliveryFee.delivery_fee === "Custom Delivery Fee" && (
              <div className="rf-input-shell rf-fee-custom">
                <span className="rf-fee-custom-k">$</span>
                <input
                  inputMode="decimal"
                  placeholder="Enter the fee"
                  autoFocus
                  value={deliveryFee.custom_delivery_fee || ""}
                  onChange={(e) =>
                    setDeliveryFee((prev) => ({
                      ...prev,
                      custom_delivery_fee: e.target.value,
                    }))
                  }
                />
              </div>
            )}
            {feeUnset && (
              <span className="rf-hint is-warn">No delivery fee on this order yet</span>
            )}
          </div>

          <div className="rf-rail-body">
            {groups.map((g) => (
              <div key={g.name}>
                <div className="rf-rail-group">
                  <span className="rf-rail-group-title">{g.name}</span>
                  <button
                    type="button"
                    className="rf-link"
                    onClick={() => setModalGroup(g.name)}
                  >
                    + Add
                  </button>
                </div>

                <div className="rf-rail-lines">
                  {g.items.map((l, index) => {
                    if (l.deleted) return null;
                    const unit = Number(l.customPrice ? l.customPrice : l.price) || 0;
                    const qty = Number(l.amount) || 1;
                    const sashFee = Number(l.sashPrice) || 0;
                    const lineTotal = unit * qty + sashFee;
                    return (
                      <div
                        key={`${l._id}-${index}`}
                        className="rf-rail-line is-clickable"
                        role="button"
                        tabIndex={0}
                        title="Open this product to see or change its details"
                        onClick={() =>
                          setEditingLine({ group: g.name, index, line: l })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setEditingLine({ group: g.name, index, line: l });
                          }
                        }}
                      >
                        <span className="rf-rail-accent" />
                        <div className="rf-rail-line-text">
                          <div className="rf-rail-line-name">
                            {l.product_name}
                          </div>
                          <div className="rf-rail-line-detail">
                            {[
                              l.code,
                              l.size,
                              l.sashPrice && !l.sash_message
                                ? "sash — no wording"
                                : "",
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                          {/* the wording itself, so the order can be read
                              off the list without opening each product */}
                          {/* how the total is made up — the sash is
                              charged once per line */}
                          {(qty > 1 || sashFee > 0) && (
                            <div className="rf-rail-line-calc">
                              {qty} × {money(unit)} each
                              {sashFee > 0 && ` + sash ${money(sashFee)}`}
                            </div>
                          )}
                          {l.sash_message && (
                            <div className="rf-rail-line-sash">
                              <span className="rf-rail-sash-k">Sash</span>
                              <span className="rf-rail-sash-v">
                                “{l.sash_message}”
                              </span>
                            </div>
                          )}
                        </div>
                        <span className="rf-rail-qty">×{l.amount}</span>
                        <span className="rf-rail-price">
                          {money(lineTotal)}
                        </span>
                        <button
                          type="button"
                          className="rf-rail-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeLine(g.name, index);
                          }}
                          aria-label="Remove"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}

                  {g.items.filter((l) => !l.deleted).length === 0 && (
                    <button
                      type="button"
                      className="rf-rail-empty"
                      onClick={() => setModalGroup(g.name)}
                    >
                      No {g.name.toLowerCase()} yet — add one
                    </button>
                  )}
                </div>
              </div>
            ))}

            <div className="rf-rail-totals">
              <div className="rf-rail-total-row">
                <span>Products (ex GST)</span>
                <span>{money(exGst)}</span>
              </div>
              <div className="rf-rail-total-row">
                <span>GST ({Math.round(gstRate * 100)}%)</span>
                <span>{money(gstAmount)}</span>
              </div>
              <div className="rf-rail-total-row">
                <span>Delivery fee</span>
                {/* mirrors the picker at the top of the rail */}
                <span className="rf-num">
                  {deliveryFee.delivery_fee ? money(feeValue) : "None"}
                </span>
              </div>
              <div className="rf-rail-total-row is-total">
                <span>Total</span>
                <span>{money(grandTotal)}</span>
              </div>
              {/* comes out of revenue, not the customer's total */}
              {!isRetail && applyFdCommission && (
                <div
                  className="rf-rail-total-row is-commission"
                  title="Paid to the funeral director out of revenue — the customer's total is unchanged"
                >
                  <span>Director commission ({pctLabel(commissionPct)})</span>
                  <span>{money(commissionAmount)}</span>
                </div>
              )}
              {!isRetail && applyFdCommission && (
                <div className="rf-rail-total-row is-net">
                  <span>Total after commission</span>
                  <span>{money(grandTotal - commissionAmount)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="rf-rail-foot">
            <button
              type="button"
              className="rf-btn rf-btn-ghost"
              onClick={() => navigate(listPath)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rf-btn rf-btn-primary rf-rail-next"
              onClick={() => {
                if (isOrderValid()) setShowSummary(true);
              }}
            >
              Review order →
            </button>
          </div>
        </aside>
      </div>

      <ProductPickerModal
        open={Boolean(modalGroup || editingLine)}
        group={(editingLine && editingLine.group) || modalGroup || "Tributes"}
        editLine={editingLine ? editingLine.line : null}
        context={[
          (editingLine && editingLine.group) || modalGroup,
          railName,
          isRetail
            ? recipient.delivery_date || recipient.frequency
            : datetime.funeral_date,
        ]
          .filter(Boolean)
          .join(" · ")}
        settings={settings}
        businessId={isEdit ? undefined : targetBusinessId}
        typeRule={pickerTypeRule}
        onAdd={addLine}
        onUpdate={updateLine}
        onClose={() => {
          setModalGroup(null);
          setEditingLine(null);
        }}
      />

    </div>
  );
}

export default NewOrderPage;
